import { useLocation, useNavigate, useRouter } from "@tanstack/react-location"
import { useEffect } from "react"
import { pageHistory } from "./pageHistory"
import { useRouteMetadata, type LocationGenerics } from "./useRouteMetadata"

/**
 * Records every page the user lands on, for the back buttons on Bundle and Event.
 *
 * Mounted once, inside the Router. Only routes marked `meta.isReturnablePage` are recorded, so
 * modal routes -- which own a route each but are not places to be sent back to -- are skipped.
 * See `pageHistory` for why the browser's own history stack can't answer this.
 */
export const usePageHistoryTracker = () => {
  const location = useLocation<LocationGenerics>()
  const router = useRouter<LocationGenerics>()
  const { getRouteParam } = useRouteMetadata()

  // Leaf match only (no `includeChildRoutes`): a modal is a child of the page it opens over, and
  // inheriting the parent's meta would record the modal as a page.
  const isReturnablePage = getRouteParam("isReturnablePage") ?? false

  const { pathname, search } = location.current

  // `search` is parsed fresh for each location, so its identity changes on navigation and not on
  // an unrelated re-render -- which is exactly when this should run.
  useEffect(() => {
    if (!isReturnablePage) return

    pageHistory.record({
      pathname: stripBasepath(pathname, router.basepath),
      search,
    })
  }, [pathname, search, isReturnablePage, router.basepath])
}

export const useNavigateBack = () => {
  const navigate = useNavigate<LocationGenerics>()

  return () => {
    const previousPage = pageHistory.getPreviousPage()

    // No recorded page means the user opened this URL directly, so there is nowhere to go back to
    // except the landing page. Never leaves ENGAGE, which the browser's back button could.
    if (!previousPage) return navigate({ to: "/" })

    navigate({ to: previousPage.pathname, search: previousPage.search })
  }
}

/**
 * Locations arrive with the tenant subdomain the Router is mounted on ("/acme/explore"), while
 * `navigate` resolves against that same basepath -- so it has to come off before we store it, or
 * back lands on "/acme/acme/explore".
 */
const stripBasepath = (pathname: string, basepath: string | undefined) => {
  if (!basepath || basepath === "/") return pathname
  if (!pathname.startsWith(basepath)) return pathname

  return pathname.slice(basepath.length) || "/"
}
